/**
 * Alpine.js Product Slider Component
 * Horizontal scrolling product carousel with navigation and touch support
 *
 * Mobile: CSS-native overflow-x scroll with scroll-snap-type: x proximity
 * Desktop: JS-driven drag carousel with transform-based positioning
 */

export default function productSlider(config = {}) {
  return {
    // Configuration
    itemsToShow: config.itemsToShow || 4,
    gap: config.gap || 16, // px
    autoScroll: config.autoScroll || false,
    autoScrollDelay: config.autoScrollDelay || 5000,

    // State
    currentIndex: 0,
    totalItems: 0,
    itemWidth: 0,
    containerWidth: 0,
    itemsPerView: 4,
    isDragging: false,
    startX: 0,
    startTranslate: 0,
    currentTranslate: 0,
    hasMoved: false,
    autoScrollInterval: null,
    initRetryCount: 0,
    maxInitRetries: 2,
    isMobile: false,
    mobileScrollLeft: 0,
    mobileMaxScroll: 0,

    // Store bound event handlers for cleanup
    boundHandlers: {
      dragStart: null,
      dragMove: null,
      dragEnd: null,
      click: null,
    },

    // Initialize component
    init() {
      // Null safety: Ensure track ref exists
      if (!this.$refs.track) return;

      this.totalItems = this.$refs.track.children.length;
      if (this.totalItems === 0) return;

      // CRITICAL: Check if element is visible/rendered
      if (this.$el.offsetWidth === 0 || this.$el.offsetHeight === 0) {
        if (this.initRetryCount < this.maxInitRetries) {
          this.initRetryCount++;
          setTimeout(() => {
            if (this.$el.offsetWidth > 0) {
              this.init();
            }
          }, 300);
        }
        return;
      }

      // Detect mobile (< 768px) for CSS scroll mode
      this.isMobile = window.matchMedia('(max-width: 767px)').matches;

      if (this.isMobile) {
        this.setupMobileScroll();
      } else {
        this.setupDesktopCarousel();
      }
    },

    // ========================================
    // MOBILE: CSS-native scroll with momentum
    // ========================================
    setupMobileScroll() {
      const track = this.$refs.track;
      const wrapper = track.parentElement;

      // Calculate mobile item widths (2 items visible)
      const mobileItemsPerView = 2;
      this.containerWidth = this.$el.offsetWidth;
      this.itemWidth = (this.containerWidth - this.gap * (mobileItemsPerView - 1)) / mobileItemsPerView;

      // Add CSS class to wrapper — all scroll/snap styles are in CSS
      // so Alpine's :style string bindings can't wipe them.
      if (wrapper) {
        wrapper.classList.add('slider-mobile-scroll');

        // Track scroll position for nav button visibility
        this.mobileMaxScroll = wrapper.scrollWidth - wrapper.clientWidth;
        this.boundHandlers.mobileScroll = () => {
          this.mobileScrollLeft = wrapper.scrollLeft;
          this.mobileMaxScroll = wrapper.scrollWidth - wrapper.clientWidth;
        };
        wrapper.addEventListener('scroll', this.boundHandlers.mobileScroll, { passive: true });
      }
    },

    // ========================================
    // DESKTOP: JS-driven drag carousel
    // ========================================
    setupDesktopCarousel() {
      // CRITICAL: Disable native drag on all images and links
      const images = this.$refs.track.querySelectorAll('img');
      const links = this.$refs.track.querySelectorAll('a');

      images.forEach(img => {
        img.setAttribute('draggable', 'false');
        img.style.pointerEvents = 'none';
      });

      links.forEach(link => {
        link.setAttribute('draggable', 'false');
      });

      // Calculate dimensions
      this.calculateDimensions();

      // Safety check after calculation
      if (this.containerWidth === 0 || this.itemWidth === 0) return;

      // Set initial position
      this.updateSlider();

      // Setup resize listener
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateDimensions();
        this.updateSlider();
      });
      this.resizeObserver.observe(this.$el);

      // Setup touch/drag events
      this.setupDragEvents();

      // Start auto-scroll if enabled
      if (this.autoScroll && this.totalItems > this.itemsPerView) {
        this.startAutoScroll();
      }
    },

    // Calculate dimensions based on screen size
    calculateDimensions() {
      // Safety check: Ensure element exists and is visible
      if (!this.$el || this.$el.offsetWidth === 0) {
        this.containerWidth = 0;
        this.itemWidth = 0;
        return;
      }

      this.containerWidth = this.$el.offsetWidth;

      // Responsive items per view (desktop only - mobile uses CSS scroll)
      if (window.innerWidth < 768) {
        this.itemsPerView = 2;
      } else if (window.innerWidth < 1024) {
        this.itemsPerView = 2.5;
      } else if (window.innerWidth < 1280) {
        this.itemsPerView = 3;
      } else if (window.innerWidth < 1536) {
        this.itemsPerView = 3.5;
      } else {
        this.itemsPerView = this.itemsToShow;
      }

      // Calculate item width including gap
      this.itemWidth =
        (this.containerWidth - this.gap * (this.itemsPerView - 1)) / this.itemsPerView;
    },

    // Setup drag/touch events (desktop only)
    setupDragEvents() {
      const track = this.$refs.track;

      // Create bound handlers for proper cleanup
      this.boundHandlers.dragStart = this.handleDragStart.bind(this);
      this.boundHandlers.dragMove = this.handleDragMove.bind(this);
      this.boundHandlers.dragEnd = this.handleDragEnd.bind(this);
      this.boundHandlers.click = e => {
        if (this.hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      // Mouse events - CRITICAL: All on document for container-exit handling
      track.addEventListener('mousedown', this.boundHandlers.dragStart, { passive: false });
      document.addEventListener('mousemove', this.boundHandlers.dragMove, { passive: false });
      document.addEventListener('mouseup', this.boundHandlers.dragEnd, { passive: false });

      // CRITICAL FIX: Multiple handlers for reliable drag release
      window.addEventListener('blur', this.boundHandlers.dragEnd);
      document.addEventListener('mouseleave', this.boundHandlers.dragEnd);
      track.addEventListener('mouseleave', this.boundHandlers.dragEnd);

      // Touch events (for tablets in landscape that aren't "mobile")
      track.addEventListener('touchstart', this.boundHandlers.dragStart, { passive: true });
      document.addEventListener('touchmove', this.boundHandlers.dragMove, { passive: true });
      document.addEventListener('touchend', this.boundHandlers.dragEnd, { passive: true });
      document.addEventListener('touchcancel', this.boundHandlers.dragEnd, { passive: true });

      // Prevent click when dragging
      track.addEventListener('click', this.boundHandlers.click, { capture: true });

      // Apply performance hints to track
      track.style.willChange = 'transform';
      track.style.cursor = 'grab';
    },

    handleDragStart(e) {
      if (e.type === 'mousedown' && e.button !== 0) return;
      if (!this.$refs.track) return;

      // CRITICAL: Prevent native image drag behavior
      e.preventDefault();

      if (this.autoScroll) {
        this.pauseAutoScroll();
      }

      this.isDragging = true;
      this.hasMoved = false;
      this.startX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
      this.startTranslate = this.currentTranslate;

      this.$refs.track.style.cursor = 'grabbing';

      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    },

    handleDragMove(e) {
      if (!this.isDragging) return;

      e.preventDefault();

      const currentX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
      const diff = currentX - this.startX;

      if (Math.abs(diff) > 5) {
        this.hasMoved = true;
      }

      this.currentTranslate = this.startTranslate + diff;
      this.setTransform(this.currentTranslate, false);
    },

    handleDragEnd(e) {
      if (!this.isDragging) return;
      if (!this.$refs.track) return;

      if (e) e.preventDefault();

      this.isDragging = false;

      this.$refs.track.style.cursor = 'grab';

      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';

      // Snap to nearest item with smooth transition
      const itemWidthWithGap = this.itemWidth + this.gap;
      const newIndex = Math.round(-this.currentTranslate / itemWidthWithGap);
      this.currentIndex = Math.max(0, Math.min(newIndex, this.getMaxIndex()));

      this.updateSlider();

      if (this.hasMoved) {
        setTimeout(() => {
          this.hasMoved = false;
        }, 100);
      }

      if (this.autoScroll) {
        this.resumeAutoScroll();
      }
    },

    // Navigation: Previous
    prev() {
      if (!this.canGoPrev()) return;
      if (this.isMobile) {
        const wrapper = this.$refs.track.parentElement;
        this.smoothScrollTo(wrapper, wrapper.scrollLeft - (this.itemWidth + this.gap));
        return;
      }
      this.currentIndex = Math.max(0, this.currentIndex - 1);
      this.updateSlider();
    },

    // Navigation: Next
    next() {
      if (!this.canGoNext()) return;
      if (this.isMobile) {
        const wrapper = this.$refs.track.parentElement;
        this.smoothScrollTo(wrapper, wrapper.scrollLeft + (this.itemWidth + this.gap));
        return;
      }
      this.currentIndex = Math.min(this.getMaxIndex(), this.currentIndex + 1);
      this.updateSlider();
    },

    // Manual smooth scroll (Chrome bug: scrollBy smooth fails with scroll-snap)
    smoothScrollTo(el, target, duration = 300) {
      const start = el.scrollLeft;
      const delta = target - start;
      const startTime = performance.now();
      const ease = t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
      const step = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        el.scrollLeft = start + delta * ease(progress);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    },

    // Navigation: Go to specific slide
    goToSlide(index) {
      if (this.isMobile) return;
      this.currentIndex = Math.max(0, Math.min(index, this.getMaxIndex()));
      this.updateSlider();
    },

    // Check if can go previous
    canGoPrev() {
      if (this.isMobile) return this.mobileScrollLeft > 2;
      return this.currentIndex > 0;
    },

    // Check if can go next
    canGoNext() {
      if (this.isMobile) return this.mobileScrollLeft < this.mobileMaxScroll - 2;
      return this.currentIndex < this.getMaxIndex();
    },

    // Get maximum scroll index
    getMaxIndex() {
      const itemsPerView = Math.floor(this.itemsPerView);
      return Math.max(0, this.totalItems - itemsPerView);
    },

    // Update slider position (desktop only)
    updateSlider() {
      if (this.isMobile) return;

      const itemWidthWithGap = this.itemWidth + this.gap;
      this.currentTranslate = -(this.currentIndex * itemWidthWithGap);

      const maxTranslate = -(this.getMaxIndex() * itemWidthWithGap);
      this.currentTranslate = Math.max(maxTranslate, this.currentTranslate);

      this.setTransform(this.currentTranslate, true);
    },

    // Set transform with transition
    setTransform(value, withTransition) {
      const track = this.$refs.track;
      if (!track) return;

      track.style.transform = `translate3d(${value}px, 0, 0)`;

      if (withTransition) {
        track.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      } else {
        track.style.transition = 'none';
      }
    },

    // Get transform style for Alpine binding
    getTransform() {
      if (this.isMobile) return 'none';
      return `translateX(${this.currentTranslate}px)`;
    },

    // Show/hide navigation based on content overflow
    shouldShowNav() {
      if (this.isMobile) return false;
      return this.totalItems > Math.floor(this.itemsPerView);
    },

    // Auto-scroll functionality
    startAutoScroll() {
      this.autoScrollInterval = setInterval(() => {
        if (this.currentIndex >= this.getMaxIndex()) {
          this.currentIndex = 0;
        } else {
          this.next();
        }
      }, this.autoScrollDelay);
    },

    stopAutoScroll() {
      if (this.autoScrollInterval) {
        clearInterval(this.autoScrollInterval);
        this.autoScrollInterval = null;
      }
    },

    pauseAutoScroll() {
      this.stopAutoScroll();
    },

    resumeAutoScroll() {
      if (this.autoScroll && this.totalItems > this.itemsPerView) {
        this.startAutoScroll();
      }
    },

    // Cleanup on destroy
    destroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      this.stopAutoScroll();

      const track = this.$refs.track;
      if (track && this.boundHandlers.dragStart) {
        track.removeEventListener('mousedown', this.boundHandlers.dragStart);
        document.removeEventListener('mousemove', this.boundHandlers.dragMove);
        document.removeEventListener('mouseup', this.boundHandlers.dragEnd);

        window.removeEventListener('blur', this.boundHandlers.dragEnd);
        document.removeEventListener('mouseleave', this.boundHandlers.dragEnd);
        track.removeEventListener('mouseleave', this.boundHandlers.dragEnd);

        track.removeEventListener('touchstart', this.boundHandlers.dragStart);
        document.removeEventListener('touchmove', this.boundHandlers.dragMove);
        document.removeEventListener('touchend', this.boundHandlers.dragEnd);
        document.removeEventListener('touchcancel', this.boundHandlers.dragEnd);

        track.removeEventListener('click', this.boundHandlers.click);
      }

      if (track) {
        track.style.willChange = 'auto';
        track.style.cursor = '';
        track.style.transform = '';
        track.style.transition = '';

        // Clean up wrapper (mobile scroll container)
        const wrapper = track.parentElement;
        if (wrapper) {
          wrapper.classList.remove('slider-mobile-scroll');
          if (this.boundHandlers.mobileScroll) {
            wrapper.removeEventListener('scroll', this.boundHandlers.mobileScroll);
          }
        }
      }

      document.body.style.userSelect = '';
    },
  };
}
